/** Canonical site locale used when none can be resolved from URL, cookie, or IP. */
export const DEFAULT_SITE_LOCALE = "en-gb";

/** Every locale code the site serves. Must be kept in sync with i18n.locales in nuxt.config.ts. */
export const SITE_LOCALE_CODES = [
  "en",
  "en-ca",
  "en-co",
  "es-co",
  "en-cr",
  "es-cr",
  "en-es",
  "es",
  "es-es",
  "en-ge",
  "en-gb",
  "en-us",
  "en-hk",
  "en-hu",
  "hu-hu",
  "en-in",
  "en-ie",
  "en-au",
  "en-pa",
  "es-pa",
  "en-pe",
  "es-pe",
  "en-pl",
  "pl-pl",
  "en-pt",
  "pt-pt",
  "en-ro",
  "en-za",
  "en-ae",
  "en-sa",
  "en-om",
  "en-kw",
  "en-bh",
  "en-qa",
  "en-ke",
  "en-ng",
] as const;

/**
 * Applies the site's trailing-slash policy to a URL path: locale homepages
 * always end in a slash (/en-gb/), every other path never does.
 */
export function normalizeTrailingSlash(path: string): string {
  const stripped = path.replace(/\/+$/, "");
  if (stripped === "") return "/";
  const isLocaleRoot =
    stripped.lastIndexOf("/") === 0 && (SITE_LOCALE_CODES as readonly string[]).includes(stripped.slice(1));
  return isLocaleRoot ? `${stripped}/` : stripped;
}

/**
 * Applies the trailing-slash policy to a canonical URL, which may be absolute
 * (https://host/path) or a bare path, leaving any query string or hash intact.
 */
export function normalizeCanonicalUrl(url: string): string {
  const suffixIndex = url.search(/[?#]/);
  const base = suffixIndex === -1 ? url : url.slice(0, suffixIndex);
  const suffix = suffixIndex === -1 ? "" : url.slice(suffixIndex);
  const match = base.match(/^(https?:\/\/[^/]*)(\/.*)?$/);
  const origin = match ? match[1] : "";
  const path = match ? (match[2] ?? "/") : base;
  return origin + normalizeTrailingSlash(path) + suffix;
}

/**
 * Applies the trailing-slash policy only to URLs on the site's own origin
 * (or site-relative paths); URLs on origins we don't control are returned
 * untouched, since their hosts define their own canonical form.
 */
export function normalizeSiteUrl(url: string, baseUrl?: string): string {
  if (url.startsWith("/") && !url.startsWith("//")) return normalizeCanonicalUrl(url);
  const origin = baseUrl?.replace(/\/+$/, "");
  if (origin && url.startsWith(origin) && ["", "/", "?", "#"].includes(url.charAt(origin.length))) {
    return normalizeCanonicalUrl(url);
  }
  return url;
}

/**
 * Formats an hreflang code to the conventional casing: language subtag
 * lowercase, region subtag uppercase (en-GB). Comparison is case-insensitive
 * for consumers, so this only affects how the code is rendered.
 */
export function formatHreflang(code: string): string {
  if (code.toLowerCase() === "x-default") return "x-default";
  const [lang, region] = code.split("-");
  return region ? `${lang.toLowerCase()}-${region.toUpperCase()}` : lang.toLowerCase();
}

/** Returns the configured default locale, or DEFAULT_SITE_LOCALE if it is missing. */
export function resolveSiteDefaultLocale(configured?: unknown): string {
  return typeof configured === "string" && configured.length > 0 ? configured : DEFAULT_SITE_LOCALE;
}

/** Country the Spanish blog falls back to; the English blog falls back to the site default. */
export const DEFAULT_SPANISH_SITE_LOCALE = "es-es";

/** Country codes a visitor can carry in preferredCountry (bare "en"/"es" excluded). */
const COUNTRY_CODES: readonly string[] = SITE_LOCALE_CODES.filter((code) => code.includes("-"));

/**
 * The country to render a blog page for: the visitor's own country, in the
 * page's language (es-pe on /en/blog -> en-pe). Null when the preferredCountry
 * cookie is missing or invalid, or the country has no site in that language.
 * The blog API is asked for the language in the country's prefix, so the
 * prefix must match the page: es-es on an /en/ post would request a Spanish
 * post that doesn't exist.
 */
export function resolveBlogCountry(cookieValue: unknown, lang: "en" | "es"): string | null {
  if (typeof cookieValue !== "string" || !COUNTRY_CODES.includes(cookieValue)) return null;
  const country = `${lang}-${cookieValue.slice(cookieValue.indexOf("-") + 1)}`;
  return COUNTRY_CODES.includes(country) ? country : null;
}
